import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import useSearch from "hooks/useSearch";
import { useModals } from "ModalProvider";
import { FaCircle } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { searchByProp } from "utils/search";
import { SALES_FETCH_CUSTOMERS } from "./graphql";
import NewCustomerForm from "./NewCustomerForm";

const CustomerListPage = () => {
  const [q, setQ] = useSearch();

  const { loading, error, data } = useQuery(SALES_FETCH_CUSTOMERS, {
    fetchPolicy: "network-only",
  });
  const modal = useModals();

  function tryCreateCustomer() {
    modal.present({
      title: "Create Customer",
      maxWidth: "max-w-3xl",
      center: true,
      children: <NewCustomerForm hide={modal.hide} />,
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const customers = data.customers
    .filter((i) => searchByProp(i, ["name"], q))
    .sort((a, b) => {
      // sort by odooId and then by createdAt desc
      if (a.odooPartnerId && !b.odooPartnerId) return -1;
      if (!a.odooPartnerId && b.odooPartnerId) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  return (
    <Page
      title="Customers"
      rightButtons={
        <div>
          <a
            href={process.env.REACT_APP_SERVER_ADMIN_URL + "customer/customer/"}
            target="_blank"
          >
            Admin
          </a>
        </div>
      }
      className="h-screen"
      staticHeader
    >
      <div className="p-6 flex-1 overflow-auto flex flex-col">
        <div className="flex items-center justify-between space-x-6">
          <div className="flex-1">
            <SearchBar
              placeholder="Search by name"
              initialQuery={q}
              onChange={setQ}
              autoFocus
            />
          </div>

          <Button onClick={tryCreateCustomer}>+ Create Customer</Button>
        </div>

        <div className="card mt-6 p-0 flex-1 overflow-auto">
          <table>
            <thead>
              <tr className="sticky top-0 backdrop-blur">
                <th className="py-3 px-4">Customer</th>
                <th className="py-3 px-4">Price List</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="py-2 border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <td className="px-4">
                    <div className="flex items-center space-x-2">
                      {customer.odooPartnerId && (
                        <FaCircle className="text-purple-500" />
                      )}

                      <Link to={`${customer.id}`}>{customer.name || "-"}</Link>
                    </div>
                  </td>
                  <td className="px-4">
                    {customer.priceList &&
                    customer.priceList.deletedAt === null ? (
                      <Link
                        to={`/sales/price-lists/${customer.priceList.id}`}
                        target="_blank"
                      >
                        {customer.priceList.name}
                      </Link>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 opacity-70">
          <div className="flex items-center space-x-2">
            <FaCircle className="text-purple-500" />
            <span>
              Indicates that the customer is connected to an Odoo Customer.
            </span>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CustomerListPage;
